import type { NearbyOfficeOption } from "@homeinstead/marketing-api";

import { assign, setup } from "xstate";

type OfficeSearchContext = {
  hasLookupError: boolean;
  isFetching: boolean;
  options: NearbyOfficeOption[];
  /**
   * A restored office id (from persistedFieldValues.franchiseNumber) to
   * prefer over the nearest office once the async lookup produces a real
   * result set. Cleared as soon as it has been checked against one — see
   * SYNC_LOOKUP — so it never overrides a later manual selection.
   */
  preferredOfficeId: number | null;
  selectedOptionId: string | null;
  typedLocation: string;
};

type OfficeSearchEvent =
  | { type: "TYPE"; value: string }
  | {
      type: "SYNC_LOOKUP";
      options: NearbyOfficeOption[];
      isFetching: boolean;
      hasLookupError: boolean;
    }
  | { type: "SELECT_OPTION"; optionId: string };

type OfficeSearchInitialContext = {
  typedLocation?: string;
  preferredOfficeId?: number | null;
};

const officeSearchMachine = setup({
  types: {
    context: {} as OfficeSearchContext,
    events: {} as OfficeSearchEvent,
  },
});

const createOfficeSearchMachine = (initial?: OfficeSearchInitialContext) =>
  officeSearchMachine.createMachine({
    context: {
      hasLookupError: false,
      isFetching: false,
      options: [] as NearbyOfficeOption[],
      preferredOfficeId: initial?.preferredOfficeId ?? null,
      selectedOptionId: null as string | null,
      typedLocation: initial?.typedLocation ?? "",
    },
    id: "officeSearch",
    initial: "ready",
    states: {
      ready: {
        on: {
          SELECT_OPTION: {
            actions: assign({
              selectedOptionId: ({ event }) => event.optionId,
            }),
          },
          SYNC_LOOKUP: {
            actions: assign({
              hasLookupError: ({ event }) => event.hasLookupError,
              isFetching: ({ event }) => event.isFetching,
              options: ({ event }) => event.options,
              // Retained only while the lookup is still in flight and has not
              // yet produced a real result set — cleared as soon as there is
              // something to check it against, whether that check succeeds,
              // falls back, or the lookup ends in error/empty.
              preferredOfficeId: ({ context, event }) =>
                event.isFetching && event.options.length === 0
                  ? context.preferredOfficeId
                  : null,
              selectedOptionId: ({ context, event }) => {
                if (event.options.length === 0) {
                  return null;
                }

                if (context.preferredOfficeId != null) {
                  const preferredOption = event.options.find(
                    (option) => option.officeID === context.preferredOfficeId,
                  );
                  if (preferredOption) {
                    return preferredOption.optionId;
                  }
                }

                const selectedStillExists = event.options.some(
                  (option) => option.optionId === context.selectedOptionId,
                );
                // Default to the first (nearest) office so the user can
                // continue without an extra interaction. A manual choice is
                // preserved whenever it survives the new result set.
                return selectedStillExists
                  ? context.selectedOptionId
                  : (event.options[0]?.optionId ?? null);
              },
            }),
          },
          TYPE: {
            actions: assign({
              preferredOfficeId: () => null,
              selectedOptionId: () => null,
              typedLocation: ({ event }) => event.value,
            }),
          },
        },
      },
    },
  });

export { createOfficeSearchMachine };
